import "./index.css";
import { useEffect } from "react";

import createCache from "@emotion/cache";
// eslint-disable-next-line no-restricted-imports
import { CacheProvider } from "@emotion/react";
import * as Sentry from "@sentry/react";
import { IntlProvider } from "react-intl";

import { getThemeStyles } from "@atlaskit/tokens";
import AnalyticsProviders from "@atlassian/wac-analytics-providers";
import { InitUFO } from "@atlassian/wac-init-ufo";
import getUFORouteName from "@atlassian/wac-ufo-route-name";
import UFOSegment from "@atlassian/wac-ufo-segment";
import traceUFOPageLoad from "@atlassian/wac-ufo-trace-pageload";
import { MeResponse } from "@atlassiansox/asc-stargate";

import { InitializeAutoAtlAnalytics } from "./analytics/atl-analytics";
import {
    WacAnalyticsClientProvider,
    WacAnalyticsListener
} from "./analytics/wac-analytics";
import { AppPricingData } from "./contentful";
import {
    AvailableProductsResponse,
    BxpWebStargateClientProvider,
    GetSignupUserTraitsResponse
} from "./libraries/bxp-web-stargate-client/src";
import { PageWrapper } from "./pages/page-wrapper";
import { LoggedInUserProvider } from "./utils/logged-in-user-provider";
import { UserLocale } from "./utils/useGeoCurrency";
import { parseRoute } from "./wac-routes";

import type {
    Template,
    TemplateCollection
} from "./content/templates/template.types";

export type LayoutProps = {
    location: Location;
    messages: Readonly<Record<string, string>>;
    inlineCss?: string;
    pricing?: AppPricingData;
    availableProducts?: AvailableProductsResponse;
    signupUserTraits?: GetSignupUserTraitsResponse;
    userProfile?: MeResponse;
    countryCode?: UserLocale;
    pureClientMode?: boolean;
    templates?: Template[];
    collections?: TemplateCollection[];
    themeStyles?: Awaited<ReturnType<typeof getThemeStyles>>;
    children: React.ComponentType<PageProps>;
};

export type PageProps = {
    pricing?: AppPricingData;
    availableProducts?: AvailableProductsResponse;
    signupUserTraits?: GetSignupUserTraitsResponse;
    userProfile?: MeResponse;
    countryCode?: UserLocale;
    templates?: Template[];
    collections?: TemplateCollection[];
};

const emotionCache = createCache({
    key: "emotion-prefix-key",
    stylisPlugins: []
});

export const Layout = (props: LayoutProps) => {
    const hasAutoAtlAnalytics =
        process.env["AUTO_ANALYTICS_ENABLED"] === "true";

    useEffect(() => {
        Sentry.init({
            dsn: "https://a48c5247caec7a2e0ad0b8ef86376ee1@o55978.ingest.sentry.io/4505919063457792",
            environment: process.env["CLIENT_LIBRARY_ENV"],
            integrations: [
                new Sentry.BrowserTracing({
                    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
                    tracePropagationTargets: [
                        "localhost",
                        /^https:\/\/atlassian\.com/
                    ]
                })
            ],
            // Performance Monitoring
            tracesSampleRate: 1.0, // Capture 100% of the transactions, reduce in production!
            enabled: process.env["CLIENT_LIBRARY_ENV"] !== "local"
        });
    }, []);
    const pageViewProperties: { originProduct?: string } = {};
    const baseUrl = "https://www.atlassian.com";
    const { locale, path, variant } = parseRoute(location);
    const isBrowser = () => typeof window !== "undefined";
    if (isBrowser()) {
        const ufoName = getUFORouteName(path.length ? "/" + path : "home");
        traceUFOPageLoad(ufoName, ufoName); // (ufoName, route.name);

        pageViewProperties.originProduct =
            "wac" +
            (window.location.pathname === "/"
                ? ""
                : window.location.pathname.replace(new RegExp("/", "g"), "-"));
    }

    return (
        <Sentry.ErrorBoundary
            //@ts-ignore
            beforeCapture={(scope) => {
                scope.setTag("service", "wac-web-fe");
            }}
        >
            <InitUFO />
            <CacheProvider value={emotionCache}>
                <IntlProvider locale={locale} messages={props.messages}>
                    <UFOSegment name="wac">
                        <PageWrapper
                            baseUrl={baseUrl}
                            // we don't want a trailing slash for the homepage
                            route={path.length ? "/" + path : ""}
                            variant={variant}
                            inlineCss={props.inlineCss}
                            pureClientMode={!!props.pureClientMode}
                            themeStyles={props.themeStyles}
                            templates={props.templates}
                            collections={props.collections}
                        >
                            <BxpWebStargateClientProvider
                                baseUrl={
                                    process.env[
                                        "NEXT_PUBLIC_API_PRIVATE_BASE_URL"
                                    ] as string
                                }
                            >
                                <LoggedInUserProvider>
                                    <WacAnalyticsClientProvider>
                                        <WacAnalyticsListener>
                                            <AnalyticsProviders>
                                                <props.children {...props} />
                                            </AnalyticsProviders>
                                            {/**
                                             * InitializeAutoAtlAnalytics is used to auto-capture GTM events (previously handled
                                             * by the atl-analytics GTM wrapper). The contents of that wrapper have been ported into
                                             * this repository.
                                             *
                                             * Note: this will only attach auto-analytics to elements that exist in the page
                                             * when this component is mounted, so it needs to be the last component to render.
                                             *
                                             * In its current state, this will only pick up the components that existed on
                                             * after the first render of the above Component.
                                             *
                                             * You can call Initializer.autoTrack() in a useEffect or useLayoutEffect
                                             * to attach listeners to any new buttons/links/etc. that were added dynamically.
                                             */}
                                            {hasAutoAtlAnalytics && (
                                                <InitializeAutoAtlAnalytics
                                                    pageViewProperties={
                                                        pageViewProperties
                                                    }
                                                />
                                            )}
                                        </WacAnalyticsListener>
                                    </WacAnalyticsClientProvider>
                                </LoggedInUserProvider>
                            </BxpWebStargateClientProvider>
                        </PageWrapper>
                    </UFOSegment>
                </IntlProvider>
            </CacheProvider>
        </Sentry.ErrorBoundary>
    );
};
